import React, { useId } from 'react'
import { useIntl } from 'react-intl'
import { IDropdownOption } from '@notino/react-styleguide'

// utils
import { OPENING_HOURS_STATUS, SALONS_FILTER_TYPE } from '../../utils/enums'
import { SALONS_FILTER_ITEMS_CONFIG } from '../../utils/helper'

// assets
import FilterIcon from '../../assets/icons/FilterIcon'

// components
import Chip from '../../atoms/Chip/Chip'
import ElementsSlider from '../ElementsSlider/ElementsSlider'
import VisuallyHidden from '../../atoms/VisuallyHidden/VisuallyHidden'

// types
import { FilterValuesType, Cosmetic, Language, LoadSalonsFromFiltersParams, GetSalonsFilterCountResponse, CurrencyCode } from '../../types/types'
import { ChipProps } from '../../atoms/Chip/types'

// styles
import * as SC from './SalonsChipsStyles'

// types
type SalonsChipsProps = {
	submittedFilterValues: FilterValuesType
	defaultFilterValues: FilterValuesType
	filterItemsConfig: ReturnType<typeof SALONS_FILTER_ITEMS_CONFIG>
	setFilterSidebarOpen: (open: boolean) => void
	setFilterSidebarDetailOpen: (open: boolean) => void
	setFilterSidebarDetailType: (filterSidebarDetailType: SALONS_FILTER_TYPE) => void
	setFilterDetailOpenedDirectly: (openFromList: boolean) => void
	filterSidebarOpen: boolean
	filterSidebarDetailOpen: boolean
	filterSidebarDetailType: SALONS_FILTER_TYPE | null
	filtersDrawerId: string
	filterDetailDrawerId: string
	loadSalonsFromFilters: (params: LoadSalonsFromFiltersParams) => void
	currencyCode: CurrencyCode
	currencyCodeSecondary: CurrencyCode | undefined
	cosmetics: Cosmetic[]
	languages: Language[]
	sortingOptions: IDropdownOption[]
	initialClientDataLoading?: boolean
	filterCounts: GetSalonsFilterCountResponse | undefined
}

type ChipPropsWithOrder = ChipProps & { order: number; show: boolean }

const SKELTON_COUNTS = 8

// sizes are in rem
const getSkeletons = () => {
	return (
		<>
			{/** Main open button chip skeleton */}
			<SC.Skeleton height={2} width={3} animated />
			{/** All other regular chips skeleton */}
			{Array.from(Array(SKELTON_COUNTS)).map((_, index) => {
				const width = index === 3 || index === 7 ? 6 : 5
				return <SC.Skeleton key={index} height={2} width={width} animated />
			})}
		</>
	)
}

// component
const SalonsChips = (props: SalonsChipsProps) => {
	const {
		submittedFilterValues,
		defaultFilterValues,
		filterItemsConfig,
		setFilterSidebarOpen,
		setFilterSidebarDetailOpen,
		setFilterSidebarDetailType,
		setFilterDetailOpenedDirectly,
		filterSidebarOpen,
		filterSidebarDetailOpen,
		filterSidebarDetailType,
		filtersDrawerId,
		filterDetailDrawerId,
		loadSalonsFromFilters,
		currencyCode,
		currencyCodeSecondary,
		cosmetics,
		languages,
		sortingOptions,
		initialClientDataLoading,
		filterCounts
	} = props

	const { formatMessage } = useIntl()
	const headingId = useId()
	const filtersHeading = formatMessage({ id: 'Filters', defaultMessage: 'Filters' })

	// submitted filter values
	const {
		serviceTotalPriceRange: { priceFrom, priceTo },
		hasAvailableReservationSystem,
		// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
		// hasGift,
		openingHours,
		exactRating,
		cosmeticIDs,
		languageIDs,
		orderBy,
		availableReservations: { timeSlot, dateFrom, dateTo }
	} = submittedFilterValues

	const isPriceFilterEqualToDefault =
		priceFrom === defaultFilterValues.serviceTotalPriceRange.priceFrom && priceTo === defaultFilterValues.serviceTotalPriceRange.priceTo
	const isHasAvailableRSFilterEqualToDefault = hasAvailableReservationSystem === defaultFilterValues.hasAvailableReservationSystem
	// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
	// const isHasGiftFilterEqualToDefault = hasGift === defaultFilterValues.hasGift
	const isOpeningHoursFilterEqualToDefault = openingHours === defaultFilterValues.openingHours
	const isRatingFilterEqualToDefault = exactRating.length === defaultFilterValues.exactRating.length
	const isCosmeticsFilterEqualToDefault = cosmeticIDs.length === defaultFilterValues.cosmeticIDs.length
	const isLanguagesFilterEqualToDefault = submittedFilterValues.languageIDs.length === defaultFilterValues.cosmeticIDs.length
	const isOrderByFilterEqualToDefault = submittedFilterValues.orderBy === defaultFilterValues.orderBy

	let isDateFilterEqualToDefault = true
	if (dateFrom) {
		isDateFilterEqualToDefault = defaultFilterValues.availableReservations.dateFrom
			? dateFrom.isSame(defaultFilterValues.availableReservations.dateFrom, 'date')
			: false
	}
	if (dateTo) {
		isDateFilterEqualToDefault = defaultFilterValues.availableReservations.dateTo
			? dateTo.isSame(defaultFilterValues.availableReservations.dateTo, 'date')
			: false
	}
	if (timeSlot !== defaultFilterValues.availableReservations.timeSlot) {
		isDateFilterEqualToDefault = false
	}

	const isFilterEqualToDefault =
		isPriceFilterEqualToDefault &&
		isHasAvailableRSFilterEqualToDefault &&
		// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
		// isHasGiftFilterEqualToDefault &&
		isOpeningHoursFilterEqualToDefault &&
		isRatingFilterEqualToDefault &&
		isCosmeticsFilterEqualToDefault &&
		isDateFilterEqualToDefault &&
		isLanguagesFilterEqualToDefault &&
		isOrderByFilterEqualToDefault

	const openFilterSidebar = () => {
		// opens main sidebar, not a detail, so this needs to be false
		setFilterDetailOpenedDirectly(false)
		setFilterSidebarOpen(true)
	}

	const openFilterSidebarDetail = (detailType: SALONS_FILTER_TYPE) => {
		setFilterSidebarDetailType(detailType)
		setFilterSidebarDetailOpen(true)
		setFilterDetailOpenedDirectly(true)
	}

	const getOpenFilterAriaLabel = (filterName: string) =>
		formatMessage({ id: 'Open filter for {filterName}', defaultMessage: 'Open filter for {filterName}' }, { filterName })

	const getApplyFilterAriaLabel = (filterName: string) =>
		formatMessage({ id: 'Apply filter {filterName}', defaultMessage: 'Apply filter {filterName}' }, { filterName })

	const getRemoveFilterAriaLabel = (filterName: string) =>
		formatMessage({ id: 'Remove filter {filterName}', defaultMessage: 'Remove filter {filterName}' }, { filterName })

	const getDetailExpanded = (detailType: SALONS_FILTER_TYPE) => filterSidebarDetailOpen && filterSidebarDetailType === detailType

	const getFilterChips = () => {
		const cosmeticsSelectedLabel = !isCosmeticsFilterEqualToDefault
			? filterItemsConfig[SALONS_FILTER_TYPE.COSMETICS].getSelectedTitle(cosmetics, cosmeticIDs)
			: null

		const languagesSelectedLabel = !isLanguagesFilterEqualToDefault
			? filterItemsConfig[SALONS_FILTER_TYPE.LANGUAGES].getSelectedTitle(languages, languageIDs)
			: null

		const applyHasAvailableRSFilter = () => {
			loadSalonsFromFilters({ hasAvailableReservationSystem: !hasAvailableReservationSystem })
		}

		// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
		// const applyHasGiftFilter = () => {
		// 	loadSalonsFromFilters({ hasGift: !hasGift })
		// }

		const applyOpeningHoursFilter = () => {
			loadSalonsFromFilters({ openingHoursStatus: !openingHours ? OPENING_HOURS_STATUS.OPEN : OPENING_HOURS_STATUS.ALL })
		}

		const showCosmeticsFilters = filterCounts ? !!(Object.values(filterCounts.cosmetics).some((value) => value) || cosmeticIDs.length) : true
		const showRatingsFilters = filterCounts ? !!(Object.values(filterCounts.exactRating).some((value) => value) || exactRating.length) : true
		const showLanguagesFilters = filterCounts ? !!(Object.values(filterCounts.languages).some((value) => value) || languageIDs.length) : true

		const availableRsLabel = !isHasAvailableRSFilterEqualToDefault
			? filterItemsConfig[SALONS_FILTER_TYPE.AVAILABLE_RS].getSelectedTitle(hasAvailableReservationSystem)
			: filterItemsConfig[SALONS_FILTER_TYPE.AVAILABLE_RS].title

		// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
		// const hasGiftLabel = !isHasGiftFilterEqualToDefault
		// 	? filterItemsConfig[SALONS_FILTER_TYPE.HAS_GIFT].getSelectedTitle(hasGift)
		// 	: filterItemsConfig[SALONS_FILTER_TYPE.HAS_GIFT].title

		const openingHoursLabel = !isOpeningHoursFilterEqualToDefault
			? filterItemsConfig[SALONS_FILTER_TYPE.OPENING_HOURS].getSelectedTitle(openingHours)
			: filterItemsConfig[SALONS_FILTER_TYPE.OPENING_HOURS].title

		const priceLabel = !isPriceFilterEqualToDefault
			? filterItemsConfig[SALONS_FILTER_TYPE.PRICE].getSelectedTitle({
					minPrice: defaultFilterValues.serviceTotalPriceRange.priceFrom,
					maxPrice: defaultFilterValues.serviceTotalPriceRange.priceTo,
					maxPriceSecondary: filterCounts?.priceRange.maxValueSecondary,
					priceFrom,
					priceTo,
					currencyCode,
					currencyCodeSecondary
				})
			: filterItemsConfig[SALONS_FILTER_TYPE.PRICE].title

		const ratingLabel = !isRatingFilterEqualToDefault
			? filterItemsConfig[SALONS_FILTER_TYPE.RATING].getSelectedTitle(exactRating)
			: filterItemsConfig[SALONS_FILTER_TYPE.RATING].title

		const cosmeticsLabel = cosmeticsSelectedLabel?.label || filterItemsConfig[SALONS_FILTER_TYPE.COSMETICS].title

		const dateLabel = !isDateFilterEqualToDefault
			? filterItemsConfig[SALONS_FILTER_TYPE.DATE].getSelectedTitle(timeSlot, dateFrom, dateTo)
			: filterItemsConfig[SALONS_FILTER_TYPE.DATE].title

		const languagesLabel = languagesSelectedLabel?.label || filterItemsConfig[SALONS_FILTER_TYPE.LANGUAGES].title

		const sortingLabel = !isOrderByFilterEqualToDefault
			? filterItemsConfig[SALONS_FILTER_TYPE.SORTING].getSelectedTitle(sortingOptions, orderBy, defaultFilterValues.orderBy)
			: filterItemsConfig[SALONS_FILTER_TYPE.SORTING].title

		// all filter types except "sorting", because it is already presented in salons list as select field
		const filterChips: ChipPropsWithOrder[] = [
			{
				key: SALONS_FILTER_TYPE.PRICE,
				order: 1,
				show: true,
				label: priceLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.PRICE].title,
				isSelected: !isPriceFilterEqualToDefault,
				ariaLabel: getOpenFilterAriaLabel(priceLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.PRICE].title),
				closeAriaLabel: getRemoveFilterAriaLabel(priceLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.PRICE].title),
				hasPopup: 'dialog' as const,
				expanded: getDetailExpanded(SALONS_FILTER_TYPE.PRICE),
				controlsId: filterDetailDrawerId,
				onClick: () => openFilterSidebarDetail(SALONS_FILTER_TYPE.PRICE),
				onCloseBtnClick: () => {
					loadSalonsFromFilters({ serviceTotalPriceFrom: null, serviceTotalPriceTo: null })
				}
			},
			{
				key: SALONS_FILTER_TYPE.AVAILABLE_RS,
				order: 2,
				show: true,
				label: availableRsLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.AVAILABLE_RS].title,
				isSelected: !isHasAvailableRSFilterEqualToDefault,
				ariaLabel: !isHasAvailableRSFilterEqualToDefault
					? getRemoveFilterAriaLabel(availableRsLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.AVAILABLE_RS].title)
					: getApplyFilterAriaLabel(availableRsLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.AVAILABLE_RS].title),
				closeDecorative: true,
				onClick: applyHasAvailableRSFilter
			},
			// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
			// {
			// 	key: SALONS_FILTER_TYPE.HAS_GIFT,
			// 	order: 3,
			// 	show: true,
			// 	label: hasGiftLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.HAS_GIFT].title,
			// 	isSelected: !isHasGiftFilterEqualToDefault,
			// 	ariaLabel: !isHasGiftFilterEqualToDefault
			// 		? getRemoveFilterAriaLabel(hasGiftLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.HAS_GIFT].title)
			// 		: getApplyFilterAriaLabel(hasGiftLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.HAS_GIFT].title),
			// 	closeDecorative: true,
			// 	onClick: applyHasGiftFilter
			// },
			{
				key: SALONS_FILTER_TYPE.OPENING_HOURS,
				order: 4,
				show: true,
				label: openingHoursLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.OPENING_HOURS].title,
				isSelected: !isOpeningHoursFilterEqualToDefault,
				ariaLabel: !isOpeningHoursFilterEqualToDefault
					? getRemoveFilterAriaLabel(openingHoursLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.OPENING_HOURS].title)
					: getApplyFilterAriaLabel(openingHoursLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.OPENING_HOURS].title),
				closeDecorative: true,
				onClick: applyOpeningHoursFilter
			},
			{
				key: SALONS_FILTER_TYPE.RATING,
				order: 5,
				show: showRatingsFilters,
				label: ratingLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.RATING].title,
				isSelected: !isRatingFilterEqualToDefault,
				ariaLabel: getOpenFilterAriaLabel(ratingLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.RATING].title),
				closeAriaLabel: getRemoveFilterAriaLabel(ratingLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.RATING].title),
				hasPopup: 'dialog' as const,
				expanded: getDetailExpanded(SALONS_FILTER_TYPE.RATING),
				controlsId: filterDetailDrawerId,
				onClick: () => openFilterSidebarDetail(SALONS_FILTER_TYPE.RATING),
				onCloseBtnClick: () => {
					loadSalonsFromFilters({ exactRating: defaultFilterValues.exactRating })
				}
			},
			{
				key: SALONS_FILTER_TYPE.COSMETICS,
				order: 6,
				show: showCosmeticsFilters,
				label: cosmeticsLabel,
				count: cosmeticsSelectedLabel?.count,
				isSelected: !isCosmeticsFilterEqualToDefault,
				ariaLabel: getOpenFilterAriaLabel(cosmeticsLabel),
				closeAriaLabel: getRemoveFilterAriaLabel(cosmeticsLabel),
				hasPopup: 'dialog' as const,
				expanded: getDetailExpanded(SALONS_FILTER_TYPE.COSMETICS),
				controlsId: filterDetailDrawerId,
				onClick: () => openFilterSidebarDetail(SALONS_FILTER_TYPE.COSMETICS),
				onCloseBtnClick: () => {
					loadSalonsFromFilters({ cosmeticIDs: defaultFilterValues.cosmeticIDs })
				}
			},
			{
				key: SALONS_FILTER_TYPE.DATE,
				order: 7,
				show: true,
				label: dateLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.DATE].title,
				isSelected: !isDateFilterEqualToDefault,
				ariaLabel: getOpenFilterAriaLabel(dateLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.DATE].title),
				closeAriaLabel: getRemoveFilterAriaLabel(dateLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.DATE].title),
				hasPopup: 'dialog' as const,
				expanded: getDetailExpanded(SALONS_FILTER_TYPE.DATE),
				controlsId: filterDetailDrawerId,
				onClick: () => openFilterSidebarDetail(SALONS_FILTER_TYPE.DATE),
				onCloseBtnClick: () => {
					loadSalonsFromFilters({
						avResTimeSlotDayPart: defaultFilterValues.availableReservations.timeSlot,
						avResTimeSlotDate: null,
						avResTimeSlotDateFrom: null,
						avResTimeSlotDateTo: null
					})
				}
			},
			{
				key: SALONS_FILTER_TYPE.LANGUAGES,
				order: 8,
				show: showLanguagesFilters,
				label: languagesLabel,
				count: languagesSelectedLabel?.count,
				isSelected: !isLanguagesFilterEqualToDefault,
				ariaLabel: getOpenFilterAriaLabel(languagesLabel),
				closeAriaLabel: getRemoveFilterAriaLabel(languagesLabel),
				hasPopup: 'dialog' as const,
				expanded: getDetailExpanded(SALONS_FILTER_TYPE.LANGUAGES),
				controlsId: filterDetailDrawerId,
				onClick: () => openFilterSidebarDetail(SALONS_FILTER_TYPE.LANGUAGES),
				onCloseBtnClick: () => {
					loadSalonsFromFilters({ languageIDs: defaultFilterValues.languageIDs })
				}
			},
			{
				key: SALONS_FILTER_TYPE.SORTING,
				order: 9,
				show: true,
				label: sortingLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.SORTING].title,
				isSelected: !isOrderByFilterEqualToDefault,
				ariaLabel: getOpenFilterAriaLabel(String(sortingLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.SORTING].title)),
				closeAriaLabel: getRemoveFilterAriaLabel(String(sortingLabel ?? filterItemsConfig[SALONS_FILTER_TYPE.SORTING].title)),
				hasPopup: 'dialog' as const,
				expanded: getDetailExpanded(SALONS_FILTER_TYPE.SORTING),
				controlsId: filterDetailDrawerId,
				onClick: () => openFilterSidebarDetail(SALONS_FILTER_TYPE.SORTING),
				onCloseBtnClick: () => {
					loadSalonsFromFilters({ orderBy: defaultFilterValues.orderBy })
				}
			}
		]
			.filter((chip) => chip.show)
			.sort((a, b) => {
				if (a.isSelected === b.isSelected) {
					// If both have the same isSelected value, compare by the "order" property.
					return a.order - b.order
				}
				// If isSelected is different, put the "true" values first.
				return a.isSelected ? -1 : 1
			})

		return filterChips
	}

	return (
		<>
			<SC.Divider />
			<div role='group' aria-labelledby={headingId}>
				<VisuallyHidden as='h3' id={headingId}>
					{filtersHeading}
				</VisuallyHidden>
				<ElementsSlider>
					<SC.ChipsContent>
						{initialClientDataLoading ? (
							getSkeletons()
						) : (
							<>
								{/** Main open button chip */}
								<Chip
									label={
										<span aria-hidden>
											<FilterIcon />
										</span>
									}
									isSelected={!isFilterEqualToDefault}
									isMainButtonChip
									ariaLabel={formatMessage({ id: 'Open filters', defaultMessage: 'Open filters' })}
									hasPopup={'dialog'}
									expanded={filterSidebarOpen}
									controlsId={filtersDrawerId}
									onClick={openFilterSidebar}
								/>
								{/** All other regular chips */}
								{getFilterChips().map((chipProps) => (
									<Chip {...chipProps} />
								))}
							</>
						)}
					</SC.ChipsContent>
				</ElementsSlider>
			</div>
		</>
	)
}

export default SalonsChips
